import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton } from 'src/routes/components/permission-button';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  stAvatarDefaults,
  stErrorMessage,
  stUpsertAvatarDefaults,
  StAvatarDefaults,
} from '../../../services/socialTradingService';
import { SocialTradingAvatarField } from '../SocialTradingAvatarField';

const emptyDefaults = (): StAvatarDefaults => ({
  userAvatars: ['', '', '', '', ''],
  roomOfficial: '',
  roomKol: '',
  roomAgent: '',
});

export default function SocialTradingAvatarDefaultsList() {
  const t = useTranslate();
  const notify = useNotify();
  const [form, setForm] = useState<StAvatarDefaults>(emptyDefaults());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const got = await stAvatarDefaults();
      const users = [...(got?.userAvatars || [])];
      while (users.length < 5) users.push('');
      setForm({
        userAvatars: users.slice(0, 5),
        roomOfficial: got?.roomOfficial || '',
        roomKol: got?.roomKol || '',
        roomAgent: got?.roomAgent || '',
      });
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const setUserAvatar = (index: number, url: string) => {
    const next = [...form.userAvatars];
    next[index] = url;
    setForm({ ...form, userAvatars: next });
  };

  const save = async () => {
    const userAvatars = form.userAvatars.map((u) => u.trim()).filter(Boolean);
    if (userAvatars.length === 0) {
      notify(t('module.socialTrading.avatarDefaultsUserRequired'), { type: 'warning' });
      return;
    }
    if (!form.roomOfficial.trim() || !form.roomKol.trim() || !form.roomAgent.trim()) {
      notify(t('module.socialTrading.avatarDefaultsRoomRequired'), { type: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await stUpsertAvatarDefaults({
        userAvatars,
        roomOfficial: form.roomOfficial.trim(),
        roomKol: form.roomKol.trim(),
        roomAgent: form.roomAgent.trim(),
      });
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      load();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PermissionGuard resource="st-avatar-defaults" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.avatarDefaults')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.avatarDefaults') },
          ]}
          action={
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={load} disabled={loading || saving}>
                {t('module.socialTrading.refresh')}
              </Button>
              <PermissionButton resource="st-avatar-defaults" action="update">
                <Button size="small" variant="contained" onClick={save} disabled={loading || saving}>
                  {t('module.socialTrading.save')}
                </Button>
              </PermissionButton>
            </Stack>
          }
        />

        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="subtitle1">{t('module.socialTrading.avatarDefaultsUser')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('module.socialTrading.avatarDefaultsUserHint')}
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                {form.userAvatars.map((url, i) => (
                  <SocialTradingAvatarField
                    key={i}
                    label={`${t('module.socialTrading.avatarDefaultsUser')} #${i + 1}`}
                    value={url}
                    onChange={(u) => setUserAvatar(i, u)}
                    uploadPath="/ledger/upload"
                    disabled={loading || saving}
                  />
                ))}
              </Stack>

              <Typography variant="subtitle1">{t('module.socialTrading.avatarDefaultsRoom')}</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <SocialTradingAvatarField
                  label={t('module.socialTrading.avatarDefaultsOfficial')}
                  value={form.roomOfficial}
                  onChange={(u) => setForm({ ...form, roomOfficial: u })}
                  uploadPath="/ledger/upload"
                  disabled={loading || saving}
                />
                <SocialTradingAvatarField
                  label={t('module.socialTrading.avatarDefaultsKol')}
                  value={form.roomKol}
                  onChange={(u) => setForm({ ...form, roomKol: u })}
                  uploadPath="/ledger/upload"
                  disabled={loading || saving}
                />
                <SocialTradingAvatarField
                  label={t('module.socialTrading.avatarDefaultsAgent')}
                  value={form.roomAgent}
                  onChange={(u) => setForm({ ...form, roomAgent: u })}
                  uploadPath="/ledger/upload"
                  disabled={loading || saving}
                />
              </Stack>

              <TextField
                size="small"
                label={t('module.socialTrading.avatarDefaultsOfficial')}
                value={form.roomOfficial}
                onChange={(e) => setForm({ ...form, roomOfficial: e.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                label={t('module.socialTrading.avatarDefaultsKol')}
                value={form.roomKol}
                onChange={(e) => setForm({ ...form, roomKol: e.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                label={t('module.socialTrading.avatarDefaultsAgent')}
                value={form.roomAgent}
                onChange={(e) => setForm({ ...form, roomAgent: e.target.value })}
                fullWidth
              />
            </Stack>
          </CardContent>
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
}
